// @mui
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { MobileDatePicker } from '@mui/x-date-pickers';
// utils
import { fDate } from './utils/format-time';
// hooks
import { useResponsive } from './hooks/use-responsive';
// types
import { ICalendarView } from './types/calendar';
// components
import { Tooltip, useMediaQuery, useTheme } from '@mui/material';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Iconify from 'src/components/iconify';
// ----------------------------------------------------------------------

// ----------------------------------------------------------------------

type Props = {
  date: Date;
  view: ICalendarView;
  loading: boolean;
  onToday: VoidFunction;
  onNextDate: VoidFunction;
  onPrevDate: VoidFunction;
  onOpenFilters: VoidFunction;
  onChangeView: (newView: ICalendarView) => void;
  onChangeDate?: (newDate: Date) => void;
  onOpenSchedule: () => void;
  onStaffBooking?: () => void;
  onTimeOff?: () => void;
  filtersActive?: boolean;
  isOrganizationAdmin?: boolean;
};

export default function CalendarToolbar({
  date,
  view,
  loading,
  onToday,
  onNextDate,
  onPrevDate,
  onChangeView,
  onChangeDate,
  onOpenSchedule,
  onStaffBooking,
  onTimeOff,
  onOpenFilters,
  filtersActive = false,
  isOrganizationAdmin = false,
}: Props) {
  const { t } = useTranslation();
  const theme = useTheme();
  const lgUp = useResponsive('up', 'lg');
  const xlUp = useMediaQuery(theme.breakpoints.up('xl'));
  const mdUp = useResponsive('up', 'md');
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  const viewOptions = useMemo(
    () =>
      [
        {
          value: 'dayGridMonth' as const,
          label: t('calendar.viewMonth'),
          icon: 'material-symbols:calendar-view-month',
        },
        {
          value: 'timeGridWeek' as const,
          label: t('calendar.viewWeek'),
          icon: 'ic:baseline-view-week',
        },
        {
          value: 'timeGridDay' as const,
          label: t('calendar.viewDay'),
          icon: 'ic:baseline-view-day',
        },
      ] as const,
    [t],
  );

  const renderFilters = (
    <Stack
      direction="row"
      alignItems="center"
      flex={1}
      justifyContent={lgUp ? 'flex-start' : 'center'}
      flexWrap="wrap"
      gap={1}
    >
      <IconButton
        size="small"
        onClick={() => setDatePickerOpen(true)}
        sx={{
          width: 30,
          height: 30,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '15px',
        }}
      >
        <Iconify icon="mdi:calendar-search" width={16} height={16} />
      </IconButton>

      <MobileDatePicker
        open={datePickerOpen}
        onClose={() => setDatePickerOpen(false)}
        value={date}
        onChange={(newDate) => {
          if (newDate && onChangeDate) {
            onChangeDate(newDate);
          }
          setDatePickerOpen(false);
        }}
        DialogProps={{
          sx: {
            '& .MuiDialogActions-root': { display: 'none' },
          },
        }}
        showToolbar={false}
        closeOnSelect
        renderInput={() => <div style={{ display: 'none' }} />}
      />

      <TextField
        select
        size="small"
        value={view}
        onChange={(event) => {
          const newValue = event.target.value as ICalendarView;
          onChangeView(newValue);
        }}
        SelectProps={{
          MenuProps: {
            PaperProps: {
              sx: {
                px: 1,
                maxHeight: 220,
                '& .MuiMenuItem-root': {
                  px: 1,
                  borderRadius: 0.75,
                  typography: 'body2',
                  textTransform: 'capitalize',
                },
              },
            },
          },
          sx: { textTransform: 'capitalize' },
        }}
        sx={{
          height: 30,
          '& .MuiInputBase-root': {
            height: 30,
            padding: 0,
          },
          '& .MuiSelect-select': {
            padding: '3px 12px',
            paddingRight: '32px !important',
            fontSize: '13px',
            lineHeight: '24px',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderRadius: '15px',
          },
        }}
      >
        {viewOptions.map((viewOption) => (
          <MenuItem key={viewOption.value} value={viewOption.value}>
            {viewOption.label}
          </MenuItem>
        ))}
      </TextField>
      <Button
        size="small"
        variant="outlined"
        color="inherit"
        onClick={onOpenSchedule}
        sx={{ minWidth: 60 }}
      >
        {t('calendar.openSchedule')}
      </Button>
      <Button size="small" color="inherit" variant="outlined" onClick={onToday}>
        {t('calendar.today')}
      </Button>
      <IconButton
        size="small"
        onClick={onOpenFilters}
        sx={{
          width: 30,
          height: 30,
          border: '1px solid',
          borderColor: filtersActive ? 'primary.main' : 'divider',
          borderRadius: '15px',
          backgroundColor: filtersActive ? 'primary.lighter' : 'transparent',
          color: filtersActive ? 'primary.main' : 'inherit',
          '&:hover': {
            backgroundColor: filtersActive ? 'primary.light' : 'action.hover',
          },
        }}
      >
        <Iconify icon="ic:round-filter-list" width={16} height={16} />
      </IconButton>
    </Stack>
  );

  const renderNavigation = (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="center"
      spacing={1}
      flex={1}
    >
      <IconButton onClick={onPrevDate}>
        <Iconify icon="eva:arrow-ios-back-fill" />
      </IconButton>

      <Typography
        textAlign="center"
        textTransform="capitalize"
        variant="h6"
        noWrap
      >
        {fDate(date, 'LLLL yyyy')}
      </Typography>

      <IconButton onClick={onNextDate}>
        <Iconify icon="eva:arrow-ios-forward-fill" />
      </IconButton>
    </Stack>
  );

  const renderActions = (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      flex={lgUp ? 1 : undefined}
      justifyContent="flex-end"
      flexShrink={0}
    >
      {onTimeOff &&
        (mdUp ? (
          <Button
            size="medium"
            data-testid="calendar-timeoff-open"
            onClick={onTimeOff}
            variant="contained"
            startIcon={<Iconify icon="mdi:calendar-remove" />}
            sx={{
              background: theme.palette.common.black,
              color: theme.palette.common.white,
              overflow: 'hidden',
              '& .MuiButton-startIcon': { flexShrink: 0 },
              '& span:last-of-type, & .MuiButton-endIcon + *': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
              whiteSpace: 'nowrap',
              maxWidth: 220,
            }}
          >
            {t('calendar.addTimeOff')}
          </Button>
        ) : (
          <Tooltip
            title={
              isOrganizationAdmin
                ? t('calendar.toolbarOrgUnavailability')
                : t('calendar.toolbarProUnavailability')
            }
            placement="top"
          >
            <IconButton
              data-testid="calendar-timeoff-open"
              size="medium"
              onClick={onTimeOff}
              color="inherit"
              sx={{
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Iconify icon="mdi:calendar-remove" />
            </IconButton>
          </Tooltip>
        ))}
      {onStaffBooking &&
        (mdUp ? (
          <Button
            size="medium"
            onClick={onStaffBooking}
            variant="contained"
            color="primary"
            startIcon={<Iconify icon="mdi:calendar-plus" />}
            sx={{
              whiteSpace: 'nowrap',
              maxWidth: 180,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {t('visit.bookVisit')}
          </Button>
        ) : (
          <Tooltip title={t('visit.bookVisit')} placement="top">
            <IconButton
              onClick={onStaffBooking}
              color="inherit"
              size="medium"
              sx={{
                bgcolor: theme.palette.common.black,
                color: theme.palette.common.white,
                '&:hover': {
                  bgcolor: theme.palette.grey[800],
                },
              }}
            >
              <Iconify icon="mdi:calendar-plus" />
            </IconButton>
          </Tooltip>
        ))}
    </Stack>
  );

  if (!lgUp) {
    return (
      <Stack sx={{ p: 2.5, pr: 2, position: 'relative', gap: 2 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          sx={{ position: 'relative' }}
        >
          {renderNavigation}
        </Stack>

        {/* On screens below lg, show actions in a separate row */}
        <Stack direction="row" justifyContent="center">
          {renderActions}
        </Stack>

        {renderFilters}

        {loading && (
          <LinearProgress
            color="inherit"
            sx={{
              height: 2,
              width: 1,
              position: 'absolute',
              bottom: 0,
              left: 0,
            }}
          />
        )}
      </Stack>
    );
  }

  if (!xlUp) {
    return (
      <Stack sx={{ p: 2.5, pr: 2, position: 'relative', gap: 1.5 }}>
        <Stack direction="row" alignItems="center" justifyContent="center">
          {renderNavigation}
        </Stack>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap={1}
        >
          {renderFilters}
          {renderActions}
        </Stack>
        {loading && (
          <LinearProgress
            color="inherit"
            sx={{
              height: 2,
              width: 1,
              position: 'absolute',
              bottom: 0,
              left: 0,
            }}
          />
        )}
      </Stack>
    );
  }

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ p: 2.5, pr: 2, position: 'relative', gap: 1, width: 1 }}
    >
      {renderFilters}

      {renderNavigation}

      {renderActions}

      {loading && (
        <LinearProgress
          color="inherit"
          sx={{
            height: 2,
            width: 1,
            position: 'absolute',
            bottom: 0,
            left: 0,
          }}
        />
      )}
    </Stack>
  );
}
